import type { ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router';
import { displayName, useAuth } from '../lib/auth';
import { initials } from '../lib/format';

/** The Totum wordmark, from the styling guide. */
export function TotumLogo({ className = 'h-4 w-auto text-white' }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 100 17" role="img" aria-label="Totum">
      <path d="M75.294.309v9.218c0 4.576-2.89 7.472-7.302 7.472-4.433 0-7.281-2.896-7.281-7.472V.31h4.09v8.975c0 2.188.942 3.647 3.19 3.647 2.25 0 3.234-1.436 3.234-3.647V.309h4.07ZM99 .309v16.38h-4.09V9.064l-3.234 7.604h-2.698l-3.255-7.604v7.627h-4.069V.309h4.112l4.54 10.301L94.867.31H99ZM27.454 0c-4.54 0-8.416 3.78-8.416 8.511 0 4.73 3.876 8.511 8.416 8.511 4.561 0 8.416-3.758 8.416-8.51 0-4.754-3.855-8.512-8.416-8.512Zm0 12.932c-2.377 0-4.283-1.967-4.283-4.421s1.906-4.421 4.283-4.421c2.398 0 4.283 1.945 4.283 4.421s-1.885 4.421-4.283 4.421Z" />
      <path d="M27.454 0c-4.54 0-8.416 3.78-8.416 8.511 0 4.73 3.876 8.511 8.416 8.511 4.561 0 8.416-3.758 8.416-8.51 0-4.754-3.855-8.512-8.416-8.512Zm0 12.932c-2.377 0-4.283-1.967-4.283-4.421s1.906-4.421 4.283-4.421c2.398 0 4.283 1.945 4.283 4.421s-1.885 4.421-4.283 4.421ZM40.86.309a19.959 19.959 0 0 1 1.092 4.111h3.876v12.27h4.09V4.42h4.925V.31H40.86ZM0 .309A19.96 19.96 0 0 1 1.092 4.42h3.876v12.27h4.09V4.42h4.926V.31H0Z" />
    </svg>
  );
}

function TopLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded-lg text-sm transition-colors ${isActive ? 'text-white bg-gray-800' : 'text-gray-300 hover:text-white hover:bg-gray-800'}`
      }
    >
      {children}
    </NavLink>
  );
}

export function Layout() {
  const { me, logout } = useAuth();
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-full mx-auto px-4 flex justify-between items-center h-16 gap-2 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <NavLink to="/" className="flex items-center gap-3 shrink-0" aria-label="Totum Analytics home">
              <TotumLogo />
              <span className="hidden sm:inline text-gray-400 text-sm border-l border-gray-700 pl-3">Analytics</span>
            </NavLink>
            <nav className="flex items-center gap-1">
              <TopLink to="/">Sites</TopLink>
              <TopLink to="/settings">Settings</TopLink>
            </nav>
          </div>
          {me && (
            <div className="flex items-center gap-2 sm:gap-3 shrink-0">
              <span className="hidden md:inline text-sm text-gray-400" title={me.email ?? undefined}>{displayName(me)}</span>
              <button
                type="button"
                onClick={() => void logout()}
                className="text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg px-2 sm:px-3 py-1.5 transition-colors whitespace-nowrap"
              >
                Sign out
              </button>
              <div className="w-8 h-8 bg-totum-blue rounded-full flex items-center justify-center text-white text-sm font-medium" aria-hidden="true">
                {initials(displayName(me))}
              </div>
            </div>
          )}
        </div>
      </header>
      <main className="p-6 max-w-7xl mx-auto">
        <Outlet />
      </main>
    </div>
  );
}
